import { billingSettingsPages } from '@features/billing-readiness/navigation/billingSettingsRoutes';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import LibraryBooksOutlined from '@mui/icons-material/LibraryBooksOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import MedicationOutlined from '@mui/icons-material/MedicationOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import PriceChangeOutlined from '@mui/icons-material/PriceChangeOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import SwapVertOutlined from '@mui/icons-material/SwapVertOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import ViewModuleOutlined from '@mui/icons-material/ViewModuleOutlined';

import type { PracticeSettingsItem } from './PracticeSettingsShell';

export interface PracticeSettingsAccess {
  organization: boolean;
  practiceAdmin: boolean;
  members: boolean;
  subscription: boolean;
  billing: boolean;
  billingIdentities: boolean;
  compensation: boolean;
  appointmentTypes: boolean;
  appointmentTypeCatalog: boolean;
  appointmentLabels: boolean;
  selfScheduling: boolean;
  templates: boolean;
  requirements: boolean;
  prescriptions: boolean;
  importCount: number;
  memberCount: number;
  subjectLabel: string;
  subjectPlural: string;
}

export function practiceSettingsItems(access: PracticeSettingsAccess): PracticeSettingsItem[] {
  const items = [
    {
      path: '/practice/settings',
      label: 'Practice details',
      subtitle: `Manage the details ${access.subjectPlural} see about your practice.`,
      group: 'Practice',
      icon: <BusinessOutlined fontSize="small" />,
      visible: access.organization,
    },
    {
      path: '/practice/clinicians',
      label: 'Members',
      subtitle: 'Invite people to your practice and manage their access.',
      group: 'Practice',
      icon: <PeopleOutlined fontSize="small" />,
      badge: access.memberCount,
      visible: access.members,
    },
    {
      path: '/practice/subscriptions',
      label: 'Subscription',
      subtitle: 'Manage your plan, add-ons, and subscription billing.',
      group: 'Practice',
      icon: <CreditCardOutlined fontSize="small" />,
      visible: access.subscription,
    },
    {
      path: '/practice/data-import',
      label: 'Data import',
      subtitle: 'Review and manage data imported into your practice.',
      group: 'Practice',
      icon: <UploadFileOutlined fontSize="small" />,
      badge: access.importCount,
      visible: access.organization && access.importCount > 0,
    },
    {
      path: '/practice/settings/preferences',
      label: 'Preferences',
      group: 'Clinical setup',
      icon: <TuneOutlined fontSize="small" />,
      visible: access.organization,
    },
    {
      path: '/practice/settings/requirements',
      label: `${access.subjectLabel} requirements`,
      group: 'Clinical setup',
      icon: <ChecklistRounded fontSize="small" />,
      visible: access.organization && access.practiceAdmin && access.requirements,
    },
    {
      path: '/practice/settings/prescribing',
      label: 'E-prescribing',
      group: 'Clinical setup',
      icon: <MedicationOutlined fontSize="small" />,
      visible: access.organization && access.prescriptions,
    },
    {
      path: '/practice/appointment-types',
      label: 'Appointment types',
      group: 'Scheduling',
      icon: <CalendarMonthOutlined fontSize="small" />,
      visible: access.appointmentTypes,
    },
    {
      path: '/practice/appointment-types/catalog',
      label: 'Appointment type catalog',
      group: 'Scheduling',
      icon: <ViewModuleOutlined fontSize="small" />,
      visible: access.appointmentTypes && access.appointmentTypeCatalog,
    },
    {
      path: '/practice/appointment-labels',
      label: 'Appointment labels',
      group: 'Scheduling',
      icon: <LabelOutlined fontSize="small" />,
      visible: access.appointmentLabels,
    },
    {
      path: '/practice/scheduling-settings',
      label: 'Self-scheduling',
      subtitle: 'Set your availability and choose who can book appointments online.',
      group: 'Scheduling',
      icon: <EventAvailableOutlined fontSize="small" />,
      visible: access.selfScheduling,
    },
    {
      path: '/practice/settings/note-templates',
      label: 'Note Templates',
      group: 'Documentation',
      icon: <ArticleOutlined fontSize="small" />,
      visible: access.organization,
    },
    {
      path: '/practice/note-template-catalog',
      label: 'Note template catalog',
      group: 'Documentation',
      icon: <LibraryBooksOutlined fontSize="small" />,
      visible: access.templates,
    },
    {
      path: '/practice/settings/permissions',
      label: 'Permissions',
      group: 'Access',
      icon: <ShieldOutlined fontSize="small" />,
      visible: access.organization && access.practiceAdmin,
    },
    {
      path: '/practice/settings/report-access',
      label: 'Report access',
      group: 'Access',
      icon: <AssessmentOutlined fontSize="small" />,
      visible: access.organization && access.practiceAdmin,
    },
    {
      ...billingSettingsPages.overview,
      group: 'Billing',
      icon: <ReceiptLongOutlined fontSize="small" />,
      visible: access.billing,
    },
    {
      ...billingSettingsPages.identities,
      group: 'Billing',
      icon: <BadgeOutlined fontSize="small" />,
      visible: access.billingIdentities,
    },
    {
      ...billingSettingsPages.facilities,
      group: 'Billing',
      icon: <LocalHospitalOutlined fontSize="small" />,
      visible: access.billing,
    },
    {
      ...billingSettingsPages.enrollments,
      group: 'Billing',
      icon: <TaskAltOutlined fontSize="small" />,
      visible: access.billing,
    },
    {
      ...billingSettingsPages['fee-schedule'],
      group: 'Billing',
      icon: <RequestQuoteOutlined fontSize="small" />,
      visible: access.billing,
    },
    {
      ...billingSettingsPages.compensation,
      group: 'Billing',
      icon: <PriceChangeOutlined fontSize="small" />,
      visible: access.billing && access.compensation,
    },
    {
      ...billingSettingsPages['data-migration'],
      group: 'Billing',
      icon: <SwapVertOutlined fontSize="small" />,
      visible: access.billing,
    },
  ];
  return items.filter(item => item.visible);
}
